// Suspect-peak detection — objective evidence that readings are a data error.
//
// NEC 220.87 is a maximum-demand method and permits no outlier exclusion: a
// reading is never set aside because it is high. The Cook County export that
// prompted this module peaked at 43 kW in a year that otherwise never passed
// 11.5 kW, and the cause was in the data, not the building: ~34 hours of
// missing readings, then a 219 kWh day of erratic alternating values. That is
// a meter that stored usage during a communication outage and reported it all
// at once. Taken at face value it failed a 200 A service; excluding it, the
// calculation needed ~60 A.
//
// So each check here looks for a recognised data-error signature, never for a
// high value as such, and each returns the window a contractor may exclude
// (peakExclusions.ts). The checks only report. They change no parsing
// decision and never block an upload (context/parser.md, "What actually
// blocks an upload"). Deterministic code only — parser LOCKED #1.
//
// Timestamps are fixed-UTC wall-clock instants (fileParser.utcWallClock), so a
// calendar day is the first ten characters of the ISO string.

export type SuspectCheck =
  | 'gap_catchup'
  | 'exceeds_service_capacity'
  | 'daily_energy_spike'
  | 'utility_estimated';

export interface TimeRange {
  /** ISO instant, inclusive. */
  start: string;
  /** ISO instant, exclusive. */
  end: string;
}

export interface SuspectWindow extends TimeRange {
  /** Stable for the same readings: check and start. */
  id: string;
  check: SuspectCheck;
  hours: number;
  readingCount: number;
  /** Highest reading inside the window. */
  peakKw: number;
  peakTimestamp: string;
  /** Highest reading outside this window — the peak if it alone were excluded. */
  elsewhereKw: number;
  elsewhereTimestamp: string | null;
  /** Check-specific evidence, for the card and the telemetry. */
  detail: {
    gapHours?: number;
    gapStart?: string;
    baselineKw?: number;
    serviceKw?: number;
    dayKwh?: number;
    medianKwh?: number;
    neighbourDays?: number;
  };
}

export interface SuspectInput {
  points: Array<{ timestamp: string; kw: number }>;
  intervalMinutes: number | null;
  /** Deliverable kW of the service; null skips exceeds_service_capacity. */
  serviceKw?: number | null;
  /** Runs of readings the source itself marks as estimated. */
  estimatedRuns?: TimeRange[] | null;
}

// ─── Thresholds ──────────────────────────────────────────────────────────────
/** Missing time between two readings that opens a catch-up window. */
export const GAP_MIN_MINUTES = 180;
/** How long after a gap the meter's catch-up is looked for. */
export const CATCHUP_WINDOW_HOURS = 24;
/** Window maximum over the maximum outside every gap window. */
export const CATCHUP_RATIO = 1.5;
/** A day's energy over the median of its neighbours. */
export const DAILY_SPIKE_RATIO = 3;
/** Neighbours on each side of the day. */
export const DAILY_SPIKE_SIDE_DAYS = 7;
/** Fewer available neighbours than this and the median is not evidence. */
export const DAILY_SPIKE_MIN_NEIGHBOURS = 7;

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

interface Reading {
  t: number;
  kw: number;
  timestamp: string;
}

function sortedReadings(points: SuspectInput['points']): Reading[] {
  const out: Reading[] = [];
  for (const p of points) {
    const t = Date.parse(p.timestamp);
    if (Number.isFinite(t) && Number.isFinite(p.kw)) out.push({ t, kw: p.kw, timestamp: p.timestamp });
  }
  return out.sort((a, b) => a.t - b.t);
}

function medianOf(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Median spacing of the readings, when the caller has no interval. */
function inferIntervalMinutes(rs: Reading[]): number | null {
  const diffs: number[] = [];
  for (let i = 1; i < rs.length && diffs.length < 500; i++) {
    const d = (rs[i].t - rs[i - 1].t) / 60000;
    if (d > 0) diffs.push(d);
  }
  return diffs.length > 0 ? medianOf(diffs) : null;
}

/**
 * The US spring-forward instant of a year on the wall clock: 02:00 on the
 * second Sunday of March. A local-time export has no readings from 02:00 to
 * 03:00 that night, which is an hour of clock, not an hour of missing data.
 */
function springForwardMs(year: number): number {
  const march1 = new Date(Date.UTC(year, 2, 1));
  const firstSunday = 1 + ((7 - march1.getUTCDay()) % 7);
  return Date.UTC(year, 2, firstSunday + 7, 2, 0);
}

function crossesSpringForward(fromMs: number, toMs: number): boolean {
  const y1 = new Date(fromMs).getUTCFullYear();
  const y2 = new Date(toMs).getUTCFullYear();
  for (let y = y1; y <= y2; y++) {
    const sf = springForwardMs(y);
    if (fromMs < sf && sf < toMs) return true;
  }
  return false;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

/** Highest reading outside [start, end), with its time. */
function maxOutside(rs: Reading[], start: number, end: number): { kw: number; timestamp: string | null } {
  let kw = -Infinity;
  let timestamp: string | null = null;
  for (const r of rs) {
    if (r.t >= start && r.t < end) continue;
    if (r.kw > kw) { kw = r.kw; timestamp = r.timestamp; }
  }
  return { kw: Number.isFinite(kw) ? kw : 0, timestamp };
}

function windowOf(
  rs: Reading[],
  check: SuspectCheck,
  start: number,
  end: number,
  detail: SuspectWindow['detail']
): SuspectWindow | null {
  let peak: Reading | null = null;
  let count = 0;
  for (const r of rs) {
    if (r.t < start || r.t >= end) continue;
    count++;
    if (!peak || r.kw > peak.kw) peak = r;
  }
  if (!peak) return null;
  const elsewhere = maxOutside(rs, start, end);
  return {
    id: `${check}:${iso(start)}`,
    check,
    start: iso(start),
    end: iso(end),
    hours: (end - start) / HOUR_MS,
    readingCount: count,
    peakKw: peak.kw,
    peakTimestamp: peak.timestamp,
    elsewhereKw: elsewhere.kw,
    elsewhereTimestamp: elsewhere.timestamp,
    detail,
  };
}

/** Contiguous runs of readings satisfying `hit`, as [start, end) with end one interval past the last. */
function runsOf(rs: Reading[], intervalMs: number, hit: (r: Reading) => boolean): Array<[number, number]> {
  const runs: Array<[number, number]> = [];
  let runStart: number | null = null;
  let last = 0;
  for (const r of rs) {
    if (hit(r)) {
      if (runStart === null) runStart = r.t;
      last = r.t;
    } else if (runStart !== null) {
      runs.push([runStart, last + intervalMs]);
      runStart = null;
    }
  }
  if (runStart !== null) runs.push([runStart, last + intervalMs]);
  return runs;
}

/**
 * Runs of estimated readings, for storing on the project: the parser hands
 * back timestamps, and the project keeps the runs they form. Grouped over the
 * readings actually loaded, so a run never spans a reading that was not
 * estimated.
 */
export function estimatedRunsFrom(
  points: SuspectInput['points'],
  estimated: Iterable<string>,
  intervalMinutes: number | null
): TimeRange[] {
  const set = new Set<number>();
  for (const ts of estimated) {
    const t = Date.parse(ts);
    if (Number.isFinite(t)) set.add(t);
  }
  if (set.size === 0) return [];
  const rs = sortedReadings(points);
  const interval = intervalMinutes ?? inferIntervalMinutes(rs) ?? 15;
  return runsOf(rs, interval * 60000, r => set.has(r.t)).map(([s, e]) => ({ start: iso(s), end: iso(e) }));
}

/**
 * Regroup estimated runs from several files (and, on Add, the project's stored
 * runs) over the merged readings: every merged reading inside a run is
 * estimated, and touching runs join. Used by both upload paths, the
 * contractor's (AMIDataInput) and the homeowner's (ConsentPage).
 */
export function regroupEstimatedRuns(
  merged: SuspectInput['points'],
  runs: TimeRange[],
  intervalMinutes: number | null
): TimeRange[] {
  if (runs.length === 0) return [];
  const spans = runs.map(r => [Date.parse(r.start), Date.parse(r.end)] as const);
  const inRun = (t: number) => spans.some(([s, e]) => t >= s && t < e);
  return estimatedRunsFrom(merged, merged.filter(p => inRun(Date.parse(p.timestamp))).map(p => p.timestamp), intervalMinutes);
}

/**
 * Every window the four checks flag, oldest first. Empty for ordinary data.
 * A reading can sit in more than one window (a catch-up day is usually also
 * an energy spike); each is reported, and each is excludable on its own.
 */
export function detectSuspectWindows(input: SuspectInput): SuspectWindow[] {
  const rs = sortedReadings(input.points);
  if (rs.length < 2) return [];
  const intervalMinutes = input.intervalMinutes && input.intervalMinutes > 0
    ? input.intervalMinutes
    : inferIntervalMinutes(rs);
  if (!intervalMinutes) return [];
  const intervalMs = intervalMinutes * 60000;
  const windows: SuspectWindow[] = [];

  // 1. gap_catchup — a gap, then readings far above everything else.
  const gapWindows: Array<{ start: number; end: number; gapMinutes: number; gapStart: number }> = [];
  for (let i = 1; i < rs.length; i++) {
    let missing = (rs[i].t - rs[i - 1].t) / 60000 - intervalMinutes;
    if (crossesSpringForward(rs[i - 1].t, rs[i].t)) missing -= 60;
    if (missing < GAP_MIN_MINUTES) continue;
    const start = rs[i].t;
    const end = start + CATCHUP_WINDOW_HOURS * HOUR_MS;
    const prev = gapWindows[gapWindows.length - 1];
    if (prev && start < prev.end) {
      // A second gap inside a catch-up window: one window, the longer gap.
      prev.end = Math.max(prev.end, end);
      if (missing > prev.gapMinutes) { prev.gapMinutes = missing; prev.gapStart = rs[i - 1].t; }
      continue;
    }
    gapWindows.push({ start, end, gapMinutes: missing, gapStart: rs[i - 1].t });
  }
  if (gapWindows.length > 0) {
    let baseline = -Infinity;
    for (const r of rs) {
      if (gapWindows.some(w => r.t >= w.start && r.t < w.end)) continue;
      if (r.kw > baseline) baseline = r.kw;
    }
    if (Number.isFinite(baseline) && baseline > 0) {
      for (const g of gapWindows) {
        const w = windowOf(rs, 'gap_catchup', g.start, g.end, {
          gapHours: Math.round((g.gapMinutes / 60) * 10) / 10,
          gapStart: iso(g.gapStart),
          baselineKw: baseline,
        });
        if (w && w.peakKw > CATCHUP_RATIO * baseline) windows.push(w);
      }
    }
  }

  // 2. exceeds_service_capacity — physically impossible for this service.
  const serviceKw = input.serviceKw;
  if (serviceKw != null && serviceKw > 0) {
    for (const [s, e] of runsOf(rs, intervalMs, r => r.kw > serviceKw)) {
      const w = windowOf(rs, 'exceeds_service_capacity', s, e, { serviceKw });
      if (w) windows.push(w);
    }
  }

  // 3. daily_energy_spike — a day's energy far above the days around it, and
  //    above every ordinary day in the file. The second condition is the one
  //    real files need: in the harness corpus the neighbour rule alone flags a
  //    hot day after a week away (12 Brookes Ave, 2025-07-23: 61 kWh against a
  //    median of 18, the year's genuine peak, with a 78 kWh day nine days
  //    earlier). A meter dump outdoes every real day; a hot day does not.
  const dayKwh = new Map<number, number>();
  const spikeDays: Array<{ day: number; kwh: number; median: number; n: number }> = [];
  for (const r of rs) {
    const day = Date.UTC(
      new Date(r.t).getUTCFullYear(), new Date(r.t).getUTCMonth(), new Date(r.t).getUTCDate()
    );
    dayKwh.set(day, (dayKwh.get(day) ?? 0) + r.kw * (intervalMinutes / 60));
  }
  for (const [day, kwh] of dayKwh) {
    const neighbours: number[] = [];
    for (let k = 1; k <= DAILY_SPIKE_SIDE_DAYS; k++) {
      const before = dayKwh.get(day - k * DAY_MS);
      const after = dayKwh.get(day + k * DAY_MS);
      if (before !== undefined) neighbours.push(before);
      if (after !== undefined) neighbours.push(after);
    }
    if (neighbours.length < DAILY_SPIKE_MIN_NEIGHBOURS) continue;
    const median = medianOf(neighbours);
    if (!(median > 0) || kwh <= DAILY_SPIKE_RATIO * median) continue;
    spikeDays.push({ day, kwh, median, n: neighbours.length });
  }
  let ordinaryMaxKwh = 0;
  for (const [day, kwh] of dayKwh) {
    if (!spikeDays.some(d => d.day === day) && kwh > ordinaryMaxKwh) ordinaryMaxKwh = kwh;
  }
  for (const d of spikeDays) {
    if (d.kwh <= ordinaryMaxKwh) continue;
    const w = windowOf(rs, 'daily_energy_spike', d.day, d.day + DAY_MS, {
      dayKwh: Math.round(d.kwh * 100) / 100,
      medianKwh: Math.round(d.median * 100) / 100,
      neighbourDays: d.n,
    });
    if (w) windows.push(w);
  }

  // 4. utility_estimated — the source itself says these were not measured.
  for (const run of input.estimatedRuns ?? []) {
    const s = Date.parse(run.start);
    const e = Date.parse(run.end);
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
    const w = windowOf(rs, 'utility_estimated', s, e, {});
    if (w) windows.push(w);
  }

  // Only a window that holds the peak is reported: its highest reading must be
  // above every reading outside all the candidate windows. A window below that
  // cannot change the calculation, and the flag's one sentence — "a possible
  // meter data error affects this building's peak" — would be false for it.
  // In the harness corpus this is what keeps Donnelly's estimated zeros and a
  // dozen low-use-week spike days on two solar homes from raising a card.
  const clean = cleanPeak(rs, windows);
  return windows
    .filter(w => w.peakKw > clean)
    .sort((a, b) => a.start.localeCompare(b.start) || a.check.localeCompare(b.check));
}

/** Highest reading outside every window. */
function cleanPeak(rs: Reading[], windows: TimeRange[]): number {
  const ranges = windows.map(w => [Date.parse(w.start), Date.parse(w.end)] as const);
  let kw = -Infinity;
  for (const r of rs) {
    if (ranges.some(([s, e]) => r.t >= s && r.t < e)) continue;
    if (r.kw > kw) kw = r.kw;
  }
  return kw;
}

/**
 * What the service can deliver, in kW, from the Service Info rating: single
 * phase volts x amps, three phase volts x amps x 1.732. The project's own
 * line-to-line volts when set (the Service Configuration picker writes them),
 * else 240 single-phase and 208 three-phase. Null when no rating is entered.
 */
export function serviceDeliverableKw(project: {
  service_rating_amps?: number | null;
  service_voltage_numeric?: number | null;
  service_phase?: string | null;
}): number | null {
  const amps = project.service_rating_amps;
  if (amps == null || !(amps > 0)) return null;
  const three = project.service_phase === 'three';
  const volts = project.service_voltage_numeric && project.service_voltage_numeric > 0
    ? project.service_voltage_numeric
    : three ? 208 : 240;
  return (amps * volts * (three ? 1.732 : 1)) / 1000;
}
